// Game constants for Hokm

import { AnimationEvent, AnimationTimeoutMs } from './types/animation';

// Points needed to win the game
export const POINTS_TO_WIN = 7;

// Game length options (points to win)
export type GameLength = 3 | 7;
export const DEFAULT_GAME_LENGTH: GameLength = 7;

// Tricks needed to win a hand
export const TRICKS_TO_WIN_HAND = 7;

// Total tricks in a hand (depends on player count)
export const TOTAL_TRICKS_4P = 13;
export const TOTAL_TRICKS_3P = 17; // 51 cards / 3 players
export const TOTAL_TRICKS_2P = 13;
export const TOTAL_TRICKS = 13; // Default for backwards compatibility

// Number of players (default for backwards compatibility)
export const NUM_PLAYERS = 4;

// Cards per player (depends on player count)
export const CARDS_PER_PLAYER_4P = 13;
export const CARDS_PER_PLAYER_3P = 17;
export const CARDS_PER_PLAYER_2P = 13;
export const CARDS_PER_PLAYER = 13; // Default for backwards compatibility

// Deck size
export const DECK_SIZE_4P = 52;
export const DECK_SIZE_3P = 51; // Remove one 2
export const DECK_SIZE_2P = 52;

// Initial deal for trump selection (all modes)
export const INITIAL_DEAL_COUNT = 5;

// Remaining cards after initial deal (4-player)
export const REMAINING_DEAL_COUNT = 8; // 4 + 4

// Dealing batch sizes per mode
// 4-player: 5 (hakem) -> trump -> 5 (others) -> 4 (all) -> 4 (all)
export const DEAL_BATCHES_4P = [5, 4, 4] as const; // After trump: 5 to others, then 4, then 4 to all
// 3-player: 5 (hakem) -> trump -> 5 (others) -> 4 (all) -> 4 (all) -> 4 (all)
export const DEAL_BATCHES_3P = [5, 4, 4, 4] as const;
// 2-player: handled differently with discard + stock draw

// 2-player specific constants
export const HAKEM_INITIAL_CARDS_2P = 8; // Hakem gets 8 cards before trump selection
export const OPPONENT_INITIAL_CARDS_2P = 5; // Opponent gets 5 cards after trump selection
export const HAKEM_DISCARD_COUNT = 3; // Hakem discards 3 cards after trump selection
export const OPPONENT_DISCARD_COUNT = 2; // Opponent discards 2 cards

// Helper function to get game config by player count
export function getGameConfig(playerCount: 2 | 3 | 4) {
  switch (playerCount) {
    case 2:
      return {
        totalTricks: TOTAL_TRICKS_2P,
        cardsPerPlayer: CARDS_PER_PLAYER_2P,
        deckSize: DECK_SIZE_2P,
        hasTeams: false,
        positions: [0, 2] as const, // South and North only
      };
    case 3:
      return {
        totalTricks: TOTAL_TRICKS_3P,
        cardsPerPlayer: CARDS_PER_PLAYER_3P,
        deckSize: DECK_SIZE_3P,
        hasTeams: false,
        positions: [0, 1, 2] as const, // South, West, North
      };
    case 4:
    default:
      return {
        totalTricks: TOTAL_TRICKS_4P,
        cardsPerPlayer: CARDS_PER_PLAYER_4P,
        deckSize: DECK_SIZE_4P,
        hasTeams: true,
        positions: [0, 1, 2, 3] as const, // All positions
      };
  }
}

// Scoring
export const POINTS_NORMAL_WIN = 1;
export const POINTS_KOT = 2; // Hakem's team wins all tricks
export const POINTS_HAKEM_KOTI = 3; // Opponents sweep Hakem's team

// Timing (milliseconds)
export const DEFAULT_TURN_TIMEOUT = 30000; // 30 seconds per turn
export const AI_TURN_DELAY = 500; // Delay before AI plays
export const TRICK_DISPLAY_DURATION = 1500; // How long to show completed trick
export const HAND_END_DISPLAY_DURATION = 3000; // How long to show hand results

// Dealing animation timing (legacy - will be removed)
export const DEAL_CARD_DELAY = 150; // Delay between individual cards
export const DEAL_BATCH_DELAY = 500; // Delay between dealing batches
export const STOCK_DRAW_DELAY = 200; // Delay between stock draws (2-player)
export const HAKEM_REVEAL_DELAY = 450; // Delay between hakem selection card reveals (must exceed client animation)

// Client-side animation stagger (visual only, doesn't affect server timing)
export const CARD_DEAL_STAGGER_MS = 75; // Delay between cards within same batch for visual stagger

// Animation timeout fallbacks (used if no client acknowledges)
export const ANIMATION_TIMEOUTS: Record<AnimationEvent, AnimationTimeoutMs> = {
  [AnimationEvent.HAKEM_CARD_REVEAL]: 800 as AnimationTimeoutMs,
  [AnimationEvent.HAKEM_SELECTED]: 1200 as AnimationTimeoutMs,
  [AnimationEvent.PARTNER_SELECTED]: 1200 as AnimationTimeoutMs,
  [AnimationEvent.SEATS_SWAPPED]: 1500 as AnimationTimeoutMs,
  [AnimationEvent.DEAL_BATCH]: 2000 as AnimationTimeoutMs,
  [AnimationEvent.TRUMP_SELECTED]: 2000 as AnimationTimeoutMs,
  [AnimationEvent.TRICK_COLLECTED]: 2500 as AnimationTimeoutMs,
  [AnimationEvent.HAND_CLEARED]: 4000 as AnimationTimeoutMs,
};

// Card dimensions for rendering
export const CARD_WIDTH = 80;
export const CARD_HEIGHT = 120;
export const CARD_RADIUS = 8;

// Game canvas dimensions
export const GAME_WIDTH = 1280;
export const GAME_HEIGHT = 720;
